/**
 * Previsualización y confirmación del alta (T026 · FR-003).
 * Confirmar emite la petición — no ejecuta el aprovisionamiento.
 */
import { useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router'

import { useSesion } from '../../auth'
import { Button } from '../../components/ui/button'
import {
    emitirAltaApi,
    leerBorrador,
    leerPrevisualizacion,
    limpiarAltaLocal,
    mensajeErrorApi,
} from '../../lib/altas'

function Bloque({ titulo, children }: { titulo: string; children: React.ReactNode }) {
    return (
        <section className="space-y-2 rounded-md border border-neutral-800 p-4">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-400">{titulo}</h2>
            <div className="text-sm text-neutral-200">{children}</div>
        </section>
    )
}

export function AltaPrevisualizar() {
    const { data: sesion } = useSesion()
    const navigate = useNavigate()
    const borrador = leerBorrador()
    const prev = leerPrevisualizacion()
    const [enviando, setEnviando] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [emitida, setEmitida] = useState<{ id: number } | null>(null)
    const [esDemo, setEsDemo] = useState(true)

    if (sesion && sesion.role !== 'admin') {
        return <p className="text-sm text-neutral-500">No encontrado</p>
    }

    // ⭐ La pantalla de éxito va ANTES del redirect por borrador vacío: al emitir
    // limpiamos sessionStorage y, si este check va primero, nos echa a /nueva.
    if (emitida) {
        return (
            <div className="mx-auto max-w-xl space-y-4" data-testid="alta-emitida">
                <h1 className="text-lg font-semibold text-neutral-100">Petición emitida</h1>
                <p className="text-sm text-neutral-300">
                    Id <span data-testid="alta-id">{emitida.id}</span> · estado{' '}
                    <span data-testid="alta-estado">solicitada</span>
                </p>
                <p className="text-xs text-neutral-500" data-testid="alta-sin-ejecutar">
                    No se ha creado ningún contenedor, cuenta ni clave de configuración. Solo hay una
                    fila en la cola del panel. El aprovisionador aún no ha tocado nada.
                </p>
                <div className="flex gap-3">
                    <Button type="button" onClick={() => navigate(`/altas/${emitida.id}`)}>
                        Ver petición
                    </Button>
                    <Button type="button" variant="outline" onClick={() => navigate('/altas/nueva')}>
                        Nueva alta
                    </Button>
                </div>
            </div>
        )
    }

    if (!borrador || !prev) {
        return <Navigate to="/altas/nueva" replace />
    }

    const identidad = prev.identidad as {
        clinicName?: string
        eaCalendarId?: string
        tenantId?: string
        destinoBot?: string
        accountId?: null
    }
    const whatsapp = prev.whatsapp as { apiKey?: string; phoneNumber?: string } | null
    const recursos = (prev.recursosPrevistos as string[] | undefined) ?? []

    async function confirmar() {
        const entrada = leerBorrador()
        if (!entrada) {
            setError('Borrador perdido — vuelve a editar')
            return
        }
        setError(null)
        setEnviando(true)
        try {
            const key = `emit-${entrada.eaCalendarId}-${Date.now()}`
            const r = await emitirAltaApi(entrada, key, esDemo)
            setEmitida({ id: r.id })
            limpiarAltaLocal()
        } catch (err) {
            setError(mensajeErrorApi(err))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <div className="mx-auto max-w-3xl space-y-6" data-testid="alta-previsualizacion">
            <div>
                <Link to="/altas/nueva" className="text-xs text-neutral-500 hover:text-neutral-300">
                    ← Volver a editar
                </Link>
                <h1 className="mt-1 text-lg font-semibold text-neutral-100">Previsualización</h1>
                <p className="text-xs text-neutral-500">
                    Esto es lo que quedaría escrito. Los secretos aparecen ocultos.
                </p>
            </div>

            <Bloque titulo="Identidad">
                <dl className="grid gap-1 text-xs md:grid-cols-2">
                    <div>
                        <dt className="text-neutral-500">Nombre</dt>
                        <dd data-testid="prev-nombre">{identidad.clinicName}</dd>
                    </div>
                    <div>
                        <dt className="text-neutral-500">Slug agenda</dt>
                        <dd data-testid="prev-slug">{identidad.eaCalendarId}</dd>
                    </div>
                    <div>
                        <dt className="text-neutral-500">tenantId (bot v2)</dt>
                        <dd data-testid="prev-tenant">{identidad.tenantId ?? '—'}</dd>
                    </div>
                    <div>
                        <dt className="text-neutral-500">Destino bot</dt>
                        <dd>{identidad.destinoBot ?? 'tenant v2'}</dd>
                    </div>
                    <div>
                        <dt className="text-neutral-500">accountId</dt>
                        <dd>lo crea la saga</dd>
                    </div>
                </dl>
            </Bloque>

            <Bloque titulo="Recursos previstos">
                <ul className="list-inside list-disc space-y-1 text-xs text-neutral-400">
                    {recursos.map((r) => (
                        <li key={r}>{r}</li>
                    ))}
                </ul>
            </Bloque>

            {whatsapp && (
                <Bloque titulo="WhatsApp">
                    <p className="text-xs">
                        Número: {whatsapp.phoneNumber} · API key:{' '}
                        <span data-testid="prev-api-key">{whatsapp.apiKey}</span>
                    </p>
                </Bloque>
            )}

            <label className="flex items-center gap-2 text-xs text-neutral-400">
                <input
                    type="checkbox"
                    checked={esDemo}
                    onChange={(e) => setEsDemo(e.target.checked)}
                />
                Alta de demo (desechable; no cruza el punto de no retorno)
            </label>

            {error && (
                <p role="alert" className="text-xs text-red-400">
                    {error}
                </p>
            )}

            <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => navigate('/altas/nueva')}>
                    Volver a editar
                </Button>
                <Button
                    type="button"
                    data-testid="alta-confirmar"
                    disabled={enviando}
                    onClick={() => void confirmar()}
                >
                    {enviando ? 'Emitiendo…' : 'Confirmar y emitir'}
                </Button>
            </div>
        </div>
    )
}
